import type { AppRecord } from "@yahshi/contracts";

export const USER_CENTER_API_DEFAULT = "https://api.yahshi.cn";

export function resolveUserCenterApiBase(env: Record<string, unknown> = import.meta.env): string {
  const value = typeof env.VITE_M1_API_BASE_URL === "string" ? env.VITE_M1_API_BASE_URL.trim().replace(/\/+$/, "") : "";
  return value || USER_CENTER_API_DEFAULT;
}

export function appUsersPath(app: Pick<AppRecord, "name">): string {
  return `/users/list?keyword=${encodeURIComponent(app.name)}`;
}

function jsonSafeName(name: string): string {
  return name.replace(/["\\\n\r]/g, "");
}

export type IntegrationNoteEndpoint = {
  method?: string;
  path: string;
};

export type IntegrationNoteLink = {
  label: string;
  href: string;
};

export type IntegrationNoteGroup = {
  title: string;
  summary: string;
  endpoints?: IntegrationNoteEndpoint[];
  links?: IntegrationNoteLink[];
};

function publicAdminUrl(hashPath: string): string {
  return `https://admin.yahshi.cn/#${hashPath}`;
}

export function buildAppIntegrationGuide(app: Pick<AppRecord, "id" | "name" | "code" | "status">, apiBase = USER_CENTER_API_DEFAULT) {
  const tokenCurl = [
    `API=${apiBase}`,
    "APP_KEY='你的 App Key'",
    "APP_SECRET='只保存在业务服务端'",
    "TIMESTAMP=$(date +%s)",
    "NONCE=$(uuidgen | tr 'A-Z' 'a-z' | tr -d '-')",
    "BODY='{}'",
    "SIGNATURE=$(printf '%s' \"$APP_KEY$TIMESTAMP$NONCE$BODY\" | openssl dgst -sha256 -hmac \"$APP_SECRET\" | awk '{print $2}')",
    "curl -sS \"$API/open/auth/token\" \\",
    "  -H \"content-type: application/json\" \\",
    "  -H \"x-yahshi-app-key: $APP_KEY\" \\",
    "  -H \"x-yahshi-timestamp: $TIMESTAMP\" \\",
    "  -H \"x-yahshi-nonce: $NONCE\" \\",
    "  -H \"x-yahshi-signature: $SIGNATURE\" \\",
    "  --data \"$BODY\"",
  ].join("\n");

  const loginCurl = [
    `curl -sS "${apiBase}/open/user/wechat-login" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"content-type: application/json\" \\",
    `  --data '{"code":"本次 wx.login 返回的 code","consent":true}'`,
  ].join("\n");

  const sessionCurl = [
    `curl -sS "${apiBase}/open/user/session" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\"",
  ].join("\n");

  const logoutCurl = [
    `curl -sS -X POST "${apiBase}/open/user/logout" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\"",
  ].join("\n");

  const createOrderCurl = [
    `curl -sS "${apiBase}/open/orders" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\" \\",
    "  -H \"content-type: application/json\" \\",
    `  --data '{"idempotencyKey":"idem-${jsonSafeName(app.code)}-001","amountMinor":1990,"currency":"CNY","title":"${jsonSafeName(app.name)} 会员","payload":{"sku":"vip-month","level":"member"}}'`,
  ].join("\n");

  const listOrdersCurl = [
    `curl -sS "${apiBase}/open/orders?status=all&page=1&pageSize=20" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\"",
  ].join("\n");

  const listMembershipCurl = [
    `curl -sS "${apiBase}/open/membership" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\"",
  ].join("\n");

  const grantMembershipCurl = [
    `curl -sS "${apiBase}/open/membership" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\" \\",
    "  -H \"content-type: application/json\" \\",
    `  --data '{"idempotencyKey":"idem-${jsonSafeName(app.code)}-member-001","level":"member","expiresAt":"2026-10-15T00:00:00.000Z"}'`,
  ].join("\n");

  const listMembershipPlansCurl = [
    `curl -sS "${apiBase}/open/membership/plans" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\"",
  ].join("\n");

  const getPointsCurl = [
    `curl -sS "${apiBase}/open/points" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\"",
  ].join("\n");

  const creditPointsCurl = [
    `curl -sS "${apiBase}/open/points" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\" \\",
    "  -H \"content-type: application/json\" \\",
    `  --data '{"idempotencyKey":"idem-${jsonSafeName(app.code)}-points-001","amount":100,"note":"入账"}'`,
  ].join("\n");

  const settleExternalOrderCurl = [
    `curl -sS "${apiBase}/open/orders/$ORDER_ID/settle-external" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\" \\",
    "  -H \"content-type: application/json\" \\",
    "  --data '{\"expectedVersion\":1}'",
  ].join("\n");

  const virtualPrepareCurl = [
    `curl -sS "${apiBase}/open/membership/virtual/prepare" \\`,
    '  -H "authorization: Bearer $ACCESS_TOKEN" \\',
    '  -H "x-yahshi-user-session: $USER_SESSION" \\',
    '  -H "content-type: application/json" \\',
    `  --data '{"productId":"商品 ID","idempotencyKey":"本次购买唯一键","loginCode":"本次 wx.login code"}'`,
  ].join("\n");
  const virtualSubscribeSignCurl = [
    `curl -sS "${apiBase}/open/membership/virtual/subscribe-sign" \\`,
    '  -H "authorization: Bearer $ACCESS_TOKEN" \\',
    '  -H "x-yahshi-user-session: $USER_SESSION" \\',
    '  -H "content-type: application/json" \\',
    `  --data '{"loginCode":"本次 wx.login code","scene":"android","productId":"订阅道具 ID","outContractCode":"协议号","contractAccountName":"会员"}'`,
  ].join("\n");
  const virtualSyncCurl = [
    `curl -sS "${apiBase}/open/membership/virtual/sync" \\`,
    '  -H "authorization: Bearer $ACCESS_TOKEN" \\',
    '  -H "x-yahshi-user-session: $USER_SESSION" \\',
    '  -H "content-type: application/json" \\',
    `  --data '{"purchaseId":"prepare 返回的购买 ID"}'`,
  ].join("\n");

  const publishedDocumentCurl = `curl -sS "${apiBase}/open/apps/${app.id}/content/user_agreement"`;

  const consentSnapshotCurl = [
    `curl -sS "${apiBase}/open/content/user_agreement/consent" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\"",
  ].join("\n");

  const recordConsentCurl = [
    `curl -sS "${apiBase}/open/content/user_agreement/consent" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\" \\",
    "  -H \"content-type: application/json\" \\",
    "  --data '{\"version\":1}'",
  ].join("\n");

  const publicConfigCurl = [
    `curl -sS "${apiBase}/open/config" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\"",
  ].join("\n");

  const publishedAnnouncementsCurl = `curl -sS "${apiBase}/open/apps/${app.id}/announcements"`;

  const listNotificationsCurl = [
    `curl -sS "${apiBase}/open/notifications/messages?page=1&pageSize=20" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\"",
  ].join("\n");

  const markNotificationsReadCurl = [
    `curl -sS -X POST "${apiBase}/open/notifications/messages/read-all" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"x-yahshi-user-session: $USER_SESSION\"",
  ].join("\n");

  const listAiModelsCurl = [
    `curl -sS "${apiBase}/open/ai/models" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\"",
  ].join("\n");

  const invokeAiCurl = [
    `curl -sS "${apiBase}/open/ai/invoke" \\`,
    "  -H \"authorization: Bearer $ACCESS_TOKEN\" \\",
    "  -H \"content-type: application/json\" \\",
    "  --data '{\"modelId\":\"aim_demo_text\",\"prompt\":\"写一句欢迎词\"}'",
  ].join("\n");

  const webhookVerifyNote = [
    "# 产品服务端接收 POST JSON",
    "# 请求头：x-yahshi-app-id / x-yahshi-event / x-yahshi-delivery-id / x-yahshi-timestamp / x-yahshi-signature",
    "# 签名：HMAC-SHA256(appId + timestamp + rawBody, webhookSecret) 的 64 位小写 hex",
    "printf '%s' \"$APP_ID$TIMESTAMP$RAW_BODY\" | openssl dgst -sha256 -hmac \"$WEBHOOK_SECRET\" | awk '{print $2}'",
    "# 验证时间窗、原始字节签名和正文 appId/event/deliveryId 与请求头一致；持久化 deliveryId 去重。",
    "# 虚拟购买 data.source=wechat_virtual，使用 purchaseId；收到退款后重新查询当前资产。",
    "# 仅接受 2xx；不要 3xx 重定向。事件：order.paid / order.refunded / membership.granted / membership.canceled",
  ].join("\n");

  return {
    apiBase,
    signingPayload: "app_key + timestamp + nonce + rawBody",
    signingAlgorithm: "HMAC-SHA256 hex",
    timestampWindow: "±5 分钟",
    tokenTtl: "2 小时",
    identityTypes: ["phone", "union_id", "email"] as const,
    disabled: app.status !== "active",
    virtualPrepareCurl,
    virtualSubscribeSignCurl,
    virtualSyncCurl,
    tokenCurl,
    loginCurl,
    sessionCurl,
    logoutCurl,
    createOrderCurl,
    listOrdersCurl,
    listMembershipCurl,
    grantMembershipCurl,
    listMembershipPlansCurl,
    getPointsCurl,
    creditPointsCurl,
    settleExternalOrderCurl,
    publishedDocumentCurl,
    consentSnapshotCurl,
    recordConsentCurl,
    publicConfigCurl,
    publishedAnnouncementsCurl,
    listNotificationsCurl,
    markNotificationsReadCurl,
    listAiModelsCurl,
    invokeAiCurl,
    webhookVerifyNote,
    noteGroups: [
      {
        title: "凭证与安全",
        summary: "App Secret 只放业务服务端。小程序、App 和浏览器都不要保存。OpenAPI 配有私有工作区 SDK @yahshi/membership-client；小程序使用 /miniprogram 入口，只调用自己的 BFF。",
      },
      {
        title: "换 token 和登录",
        summary: "服务端用 HMAC-SHA256 换应用 access token，再由 BFF 提交微信 code 完成认证。之后请求带应用 token，用户会话放在 x-yahshi-user-session。",
        endpoints: [
          { method: "POST", path: "/open/auth/token" },
          { method: "POST", path: "/open/user/wechat-login" },
          { method: "GET", path: "/open/user/session" },
        ],
      },
      {
        title: "同一用户",
        summary: "同一手机号或 UnionID 在不同应用登录，识别为同一个用户。登录身份也可以是邮箱。",
      },
      {
        title: "订单与支付",
        summary: "小程序现金购买使用 virtual/prepare，客户端只调 wx.requestVirtualPayment。会员订阅签约使用 virtual/subscribe-sign，再调 wx.requestSubscribeSign 或 wx.requestAppleSubscribeSign。App 与网站开通会员走等级接口，不发布虚拟支付商品。客户端弹窗成功不能作为付款凭据。",
        endpoints: [
          { method: "POST", path: "/open/membership/virtual/prepare" },
          { method: "POST", path: "/open/membership/virtual/sync" },
          { method: "GET", path: "/open/membership/purchases" },
        ],
      },
      {
        title: "会员",
        summary: "会员、积分、道具分别关联空间；可共享、独立或关闭。新接入从 state 读取有效资产、products 读取商品。展示价以分计，积分兑换成本另设 pointsPrice。",
        endpoints: [
          { method: "GET", path: "/open/membership/state" },
          { method: "GET", path: "/open/membership/products" },
        ],
      },
      {
        title: "积分",
        summary: "积分范围由当前应用关联的积分空间决定。充值走微信虚拟支付，消费用 consume、撤销用 reverse，积分兑换商品用 checkout。展示价不自动决定兑换积分。",
        endpoints: [
          { method: "GET", path: "/open/membership/state" },
          { method: "POST", path: "/open/membership/consume" },
          { method: "POST", path: "/open/membership/reverse" },
          { method: "POST", path: "/open/membership/checkout" },
        ],
      },
      {
        title: "协议",
        summary: "已发布协议 JSON 匿名可读。用户同意的 version 必须等于当前发布版本；不强制重签、不发短信。WebView 打开的是纯文本，不是富文本。",
        endpoints: [
          { method: "GET", path: `/open/apps/${app.id}/content/user_agreement` },
          { method: "GET/POST", path: "/open/content/:type/consent" },
        ],
        links: [{ label: "协议页", href: publicAdminUrl(`/public/docs/${app.id}/user_agreement`) }],
      },
      {
        title: "配置与公告",
        summary: "公开开关和参数需要应用 token，例如 MAINTENANCE_MODE。运营公告匿名可读当前生效窗口。正文是纯文本，不是富文本 CMS。",
        endpoints: [
          { method: "GET", path: "/open/config" },
          { method: "GET", path: `/open/apps/${app.id}/announcements` },
        ],
        links: [{ label: "公告页", href: publicAdminUrl(`/public/announcements/${app.id}`) }],
      },
      {
        title: "站内信",
        summary: "拉取当前应用未读或全部消息，并可标记已读。微信订阅消息、邮件和短信保持封印，不会发送。",
        endpoints: [
          { method: "GET", path: "/open/notifications/messages" },
          { method: "POST", path: "/open/notifications/messages/read-all" },
        ],
      },
      {
        title: "AI",
        summary: "模型在总后台「平台设置 · AI 模型」配置一次。产品只传 modelId 和 prompt。密钥加密保存或使用 env:// 引用，接口不回显。未配置凭据时返回演示结果；配置 OpenAI/Anthropic 兼容网关和 env:// 后，线上按该供应商真实调用。",
        endpoints: [
          { method: "GET", path: "/open/ai/models" },
          { method: "POST", path: "/open/ai/invoke" },
        ],
      },
      {
        title: "产品回调",
        summary: "在本页配置 HTTPS 地址。订单支付/退款、会员开通/取消会 POST 签名 JSON。验签用独立 webhookSecret，不是 App Secret。自动生成密钥只展示一次。回调可能重复，需按签名正文 deliveryId 去重。虚拟购买使用 purchaseId；会员退款事件后重新查询整体资产。",
      },
      {
        title: "停用",
        summary: "停用应用立即作废应用 token，暂停新回调投递；原虚拟订单仍可结算并保留事件，恢复后继续投递。已发出的 HTTP 请求可能完成。",
      },
    ] satisfies IntegrationNoteGroup[],
  };
}
